// transposes a single note (or number) to another key

const transposeNotes = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B'] as const
const transposeKeys = ['numeric', ...transposeNotes] as const
const numericSteps = ['1', '1#', '2b', '2', '2#', '3b', '3', '4', '4#', '5b', '5', '5#', '6b', '6', '6#', '7b', '7'] as const
const allNotes = [...transposeNotes, ...numericSteps] as const

type TransposeNote = typeof transposeNotes[number]
export type TransposeKey = typeof transposeKeys[number]
type NumericStep = typeof numericSteps[number]
type NoteOrStep = typeof allNotes[number]

type NoteGroup = NoteOrStep | [NoteOrStep, NoteOrStep];
type NoteMap = Record<TransposeKey, NoteGroup[]>;
type NoteIndex = { i: number | null; j: number | null };


class NoteTransposer {
  private notes: NoteGroup[] = ['C', ['C#', 'Db'], 'D', ['D#', 'Eb'], 'E', 'F', ['F#', 'Gb'], 'G', ['G#', 'Ab'], 'A', ['A#', 'Bb'], 'B'];
  private circleable_notes: NoteGroup[];
  private map: NoteMap;

  constructor() {
    this.circleable_notes = this.notes.concat(this.notes);
    this.map = this.makeMap();
    console.log(this);
  }

  keys = (): TransposeKey[] => Object.keys(this.makeMap()) as TransposeKey[];

  private makeMap = (): NoteMap => ({
    'numeric': ['1', ['1#', '2b'], '2', ['2#', '3b'], '3', '4', ['4#', '5b'], '5', ['5#', '6b'], '6', ['6#', '7b'], '7'],
    'C': this.keyNotes(0),
    'C#': this.keyNotes(1),
    'Db': this.keyNotes(1),
    'D': this.keyNotes(2),
    'D#': this.keyNotes(3),
    'Eb': this.keyNotes(3),
    'E': this.keyNotes(4),
    'F': this.keyNotes(5),
    'F#': this.keyNotes(6),
    'Gb': this.keyNotes(6),
    'G': this.keyNotes(7),
    'G#': this.keyNotes(8),
    'Ab': this.keyNotes(8),
    'A': this.keyNotes(9),
    'A#': this.keyNotes(10),
    'Bb': this.keyNotes(10),
    'B': this.keyNotes(11),
  });

  // 0 -> #
  // 1 -> b
  private enharmonicPreference: Record<TransposeKey, 0 | 1> = {
    'numeric': 1,
    'C': 0,
    'C#': 0,
    'Db': 1,
    'D': 0,
    'D#': 0,
    'Eb': 1,
    'E': 0,
    'F': 1,
    'F#': 0,
    'Gb': 1,
    'G': 0,
    'G#': 0,
    'Ab': 1,
    'A': 0,
    'A#': 0,
    'Bb': 1,
    'B': 1,
  };

  private keyNotes = (i: number): NoteGroup[] => this.circleable_notes.slice(i, i + 12);

  transpose = (from_key: TransposeKey, to_key: TransposeKey, note: NoteOrStep): NoteOrStep => {
    const { i, j } = this.indexOfNote(from_key, note);
    if (i === null) return note;

    const new_note = this.map[to_key][i];

    if (Array.isArray(new_note)) {
      const pref = j !== null ? j : this.enharmonicPreference[to_key];
      return new_note[pref];
    } else {
      return new_note;
    }
  };

  private indexOfNote = (key: TransposeKey, needle: NoteOrStep): NoteIndex => {
    const index: NoteIndex = { i: null, j: null };

    this.map[key].forEach((note_or_group, i) => {
      if (note_or_group === needle) {
        index.i = i;
      } else if (Array.isArray(note_or_group)) {
        const j = note_or_group.indexOf(needle);
        if (j > -1) {
          index.i = i;
          index.j = j;
        }
      }
    });

    return index;
  };
}

export default new NoteTransposer();
